/**
 * moduleRegistry — canonical SSoT mapping module ids to component + layout defaults.
 *
 * Cycle-101 sprint-3 Task 3.2 origin (sprint.md:306, SDD §5.1 line 349).
 * Cycle-109 v2 sprint-204 S2.2 reshape — extended from 23 → 30 canonical entries
 * with `defaultSize: { w, h }`, `subcategory`, `description`, optional `render`.
 * Mapping inventory: grimoires/loa/cycles/cycle-109/sprint-2-registry-mapping.md.
 * Canonical reference: output/design_reference/design_handoff_echelon_2026_04_26/workspace/moduleRegistry.jsx.
 *
 * Sprint-204 Q-S2.0-a resolution: extend this existing file in place rather than
 * fork to `frontend/src/components/workspace/moduleRegistry.ts`. Existing import
 * surface (`@/lib/moduleRegistry`) preserved; cycle-101 callers (ModuleCanvas,
 * ModulePicker, modePresets.schema) keep working.
 *
 * Every analytical/workspace-surface module that can appear on the V2 canvas
 * lives in exactly one registry entry. Each entry carries:
 *   * `id` — namespaced ModuleId union (closed enum, runtime via MODULE_IDS).
 *   * `category` — canonical six-set (echelon/geopolitical/natural/economy/
 *     infrastructure/other) per `moduleRegistry.jsx:48-55`. Drives the
 *     `AddModulePicker` chip-row filter (sprint-204 S2.5).
 *   * `subcategory` — operator-facing fine-grain label (e.g. 'Synthesis',
 *     'Markets'). Surfaced in `AddModulePicker` row meta and module headers.
 *   * `label` — operator-facing tile title.
 *   * `description` — operator-facing tooltip / picker subtitle.
 *   * `defaultSize: { w, h }` — placement defaults for the 12-col × 140px grid.
 *   * `component` — `LazyExoticComponent` for code-splitting. Sprint-3 (cycle-101)
 *     shipped placeholders for modules whose real impl lands later; cycle-109
 *     sprint-204 keeps the code-split path. New canonical-only entries
 *     (`nat.disasters`, `nat.wildfires`, `infra.infrastructure`, `other.*`)
 *     ship as `placeholderLazy()` — real components land in cycle-110+.
 *   * `render?` — optional context-bearing render fn for entries that need
 *     `{ inv, onOpen, onSelectSignal }` context. Sprint-204 S2.4 ModuleCanvas
 *     rebuild prefers `entry.render(ctx)` when defined, else falls back to
 *     `<entry.component />`. Both patterns coexist (see S2.0 inventory
 *     "Field-shape reconciliation" §).
 *   * `backendSourceGroups` — canonical backend source_groups per signal-catalog.md
 *     §3.A.1. Empty array ≡ not a source_group consumer (workspace surfaces,
 *     FORGE, placeholders).
 *
 * Shared-module invariant (SDD §5.2 line 367): Finance + Maritime modes both
 * render `infra.vessels` via the same VesselTracker component — no mode-aware
 * props, no variant renderer. Per-instance state (zoom/pan/selection) keyed on
 * module-placement id.
 */

import { createElement, lazy, type LazyExoticComponent, type ComponentType, type ReactNode } from 'react';

// ── ModuleId — namespaced union (30 entries) ────────────────────────────

export type ModuleId =
  // Echelon (7)
  | 'echelon.brief'
  | 'echelon.informs'
  | 'echelon.desk'
  | 'echelon.evidence'
  | 'echelon.claims'
  | 'echelon.paradox'
  | 'echelon.forge'
  // Geopolitical (6)
  | 'geo.conflicts'
  | 'geo.military'
  | 'geo.unrest'
  | 'geo.intel'
  | 'geo.displacement'
  | 'geo.cyber'
  // Natural events (4 — sprint-204 adds nat.disasters + nat.wildfires)
  | 'nat.disasters'
  | 'nat.seismic'
  | 'nat.climate'
  | 'nat.wildfires'
  // Economy & markets (5)
  | 'econ.economic'
  | 'econ.markets'
  | 'econ.trade'
  | 'econ.supply'
  | 'econ.predictions'
  // Infrastructure & transport (4 — sprint-204 adds infra.infrastructure)
  | 'infra.aviation'
  | 'infra.maritime'
  | 'infra.vessels'
  | 'infra.infrastructure'
  // Other (4 — all sprint-204 additions)
  | 'other.news'
  | 'other.research'
  | 'other.positive'
  | 'other.giving';

// ── Category — canonical six-set ────────────────────────────────────────
//
// Cycle-109 sprint-204 S2.2 collapsed cycle-101's 16 fine-grained categories
// into canonical six per moduleRegistry.jsx:48-55. The fine-grain labels
// (e.g. 'Markets', 'Cyber') survive as `subcategory` on each entry, which is
// the field the AddModulePicker subcategory chip-row consumes.

export type ModuleCategory =
  | 'echelon'
  | 'geopolitical'
  | 'natural'
  | 'economy'
  | 'infrastructure'
  | 'other';

/** Display order for category chip-rows / pickers. Matches moduleRegistry.jsx CATEGORIES export. */
export const MODULE_CATEGORY_ORDER: readonly ModuleCategory[] = [
  'echelon',
  'geopolitical',
  'natural',
  'economy',
  'infrastructure',
  'other',
] as const;

// ── Render context ──────────────────────────────────────────────────────
//
// Sprint-204 keeps this shape minimal. S2.4 (ModuleCanvas rebuild) and S3.5
// (InvestigationDrawer rewrite) refine `inv` to the concrete Investigation
// type once the drawer's data contract is locked.

export interface ModuleRenderContext {
  readonly inv?: unknown;
  readonly onOpen?: (id: string) => void;
  readonly onSelectSignal?: (id: string) => void;
}

// ── Registry entry ──────────────────────────────────────────────────────

export interface ModuleRegistryEntry {
  readonly id: ModuleId;
  readonly category: ModuleCategory;
  readonly subcategory: string;
  readonly label: string;
  readonly description: string;
  readonly defaultSize: { readonly w: number; readonly h: number };
  /**
   * Code-split component. Cycle-101 sprint-3 shipped placeholders for entries
   * whose real impl lands later; cycle-109 sprint-204 keeps the code-split
   * path for production wins.
   */
  readonly component: LazyExoticComponent<ComponentType<Record<string, never>>>;
  /**
   * Optional context-bearing render fn. When defined, sprint-204 S2.4
   * ModuleCanvas rebuild prefers `entry.render(ctx)` over `<entry.component />`
   * so investigation context (`{ inv, onOpen, onSelectSignal }`) reaches the
   * tile. Both patterns coexist.
   */
  readonly render?: (ctx?: ModuleRenderContext) => ReactNode;
  /**
   * Canonical backend source_groups per signal-catalog.md §3.A.1.
   * Empty array ≡ not a source_group consumer (e.g. workspace surfaces, FORGE,
   * canonical-only placeholders).
   * Per cycle-101.1 sprint-2 Task 2.0 (taxonomy bridge foundation).
   */
  readonly backendSourceGroups: readonly string[];
}

// ── Placeholder factory ─────────────────────────────────────────────────
//
// Canonical-only entries (no real component yet) ship via the shared
// PlaceholderModule (`v0 · mock` shell — see workspace/PlaceholderModule.tsx).
// React.lazy needs a default export, so we route via a closure factory that
// binds `{ label, category, subcategory, description }` from the registry
// entry and produces a no-arg LazyExoticComponent for `<entry.component />`.
//
// `placeholderEntry()` is the call-site sugar: pass an entry-shaped record
// without `component`/`backendSourceGroups` and get back a full
// ModuleRegistryEntry whose `component` is the bound PlaceholderModule.
// This keeps the 11 placeholder entries single-source-of-truth — no field
// duplication between the record and the factory args. Cycle-109 sprint-204
// S2.3 refactor.

type PlaceholderEntrySpec = Omit<
  ModuleRegistryEntry,
  'component' | 'backendSourceGroups'
> & {
  readonly backendSourceGroups?: readonly string[];
};

function placeholderLazy(
  spec: Pick<
    ModuleRegistryEntry,
    'label' | 'category' | 'subcategory' | 'description'
  >
): LazyExoticComponent<ComponentType<Record<string, never>>> {
  return lazy(async () => {
    const mod = await import('../components/workspace/PlaceholderModule');
    const Placeholder = mod.default;
    return {
      default: function BoundPlaceholder() {
        return Placeholder({
          label: spec.label,
          category: spec.category,
          subcategory: spec.subcategory,
          description: spec.description,
        });
      },
    };
  });
}

function placeholderEntry(spec: PlaceholderEntrySpec): ModuleRegistryEntry {
  return {
    ...spec,
    backendSourceGroups: spec.backendSourceGroups ?? [],
    component: placeholderLazy(spec),
  };
}

// ── Lazy refs for ctx-bearing modules (sprint-204 S2.8) ─────────────────
//
// Hoisted outside the registry array so each module is loaded exactly once
// across `<entry.component />` fallback path AND `entry.render(ctx)` ctx-path.
// Re-creating `lazy()` inside `render()` would force a remount every call.

type EvidenceGridProps = { investigationId?: string | null; testId?: string };
type ClaimsRowProps = { investigationId?: string | null; testId?: string };
type InvestigationDeskProps = { investigationId?: string | null; testId?: string };

const LazyEvidenceGridModule: LazyExoticComponent<ComponentType<EvidenceGridProps>> =
  lazy(async () => {
    const mod = await import('../components/workspace/modules/EvidenceGridModule');
    return { default: mod.default };
  });

const LazyClaimsRowModule: LazyExoticComponent<ComponentType<ClaimsRowProps>> =
  lazy(async () => {
    const mod = await import('../components/workspace/modules/ClaimsRowModule');
    return { default: mod.default };
  });

const LazyInvestigationDeskModule: LazyExoticComponent<ComponentType<InvestigationDeskProps>> =
  lazy(async () => {
    const mod = await import('../components/workspace/modules/InvestigationDeskModule');
    return { default: mod.default };
  });

// ── Registry ────────────────────────────────────────────────────────────

export const MODULE_REGISTRY: readonly ModuleRegistryEntry[] = [
  // ── Echelon ─────────────────────────────────────────────────────────
  {
    id: 'echelon.brief',
    category: 'echelon',
    subcategory: 'Synthesis',
    label: 'Aeon Brief',
    description: 'MiroShark-grounded synthesis of the active investigation.',
    defaultSize: { w: 7, h: 2 },
    // Cycle-106 sprint-2 swap: placeholder → real AeonBriefModule.
    // Reads OsintSignal source_group=intelligence_synthesis via useIntelligenceSynthesis.
    component: lazy(async () => {
      const mod = await import('../components/workspace/modules/AeonBriefModule');
      return { default: mod.default };
    }),
    backendSourceGroups: ['intelligence_synthesis'],
  },
  placeholderEntry({
    id: 'echelon.informs',
    category: 'echelon',
    subcategory: 'Memory',
    label: 'Informs',
    description: 'Prior knowledge nodes informing this investigation.',
    defaultSize: { w: 5, h: 2 },
  }),
  // Sprint-204 S2.9 swap: placeholder → real InvestigationDeskModule with
  // TabHeader. Wraps the existing InvestigationSections grid (cycle-086a +
  // cycle-105) with the canonical TabHeader primitive. `render(ctx)` flows
  // `ctx.inv` (focused-investigation id) so sprint-3 S3.6 page rewrite wires
  // real evidence/claims/drift/certificate sections.
  {
    id: 'echelon.desk',
    category: 'echelon',
    subcategory: 'Desk',
    label: 'Investigation Desk',
    description: 'Active state of one investigation — readiness, claims, drift.',
    defaultSize: { w: 4, h: 2 },
    component: LazyInvestigationDeskModule as unknown as LazyExoticComponent<
      ComponentType<Record<string, never>>
    >,
    render: (ctx) => {
      const inv = typeof ctx?.inv === 'string' ? ctx.inv : null;
      return createElement(LazyInvestigationDeskModule, { investigationId: inv });
    },
    backendSourceGroups: [],
  },
  // Sprint-204 S2.8 swap: placeholder → real EvidenceGridModule with TabHeader.
  // `render(ctx)` flows `ctx.inv` (focused-investigation id string) so the
  // sprint-3 S3.6 page rewrite unlocks real evidence rows. Canvas-mount fallback
  // (no ctx) renders the canonical empty state ("Focus an investigation…").
  {
    id: 'echelon.evidence',
    category: 'echelon',
    subcategory: 'Evidence',
    label: 'Evidence Grid',
    description: 'Auto-attached OSINT evidence items with confidence.',
    defaultSize: { w: 4, h: 2 },
    component: LazyEvidenceGridModule as unknown as LazyExoticComponent<
      ComponentType<Record<string, never>>
    >,
    render: (ctx) => {
      const inv = typeof ctx?.inv === 'string' ? ctx.inv : null;
      return createElement(LazyEvidenceGridModule, { investigationId: inv });
    },
    backendSourceGroups: [],
  },
  // Sprint-204 S2.8 swap: placeholder → real ClaimsRowModule with TabHeader.
  {
    id: 'echelon.claims',
    category: 'echelon',
    subcategory: 'Claims',
    label: 'Claims Register',
    description: 'Resolvable binary claims attached to the investigation.',
    defaultSize: { w: 7, h: 2 },
    component: LazyClaimsRowModule as unknown as LazyExoticComponent<
      ComponentType<Record<string, never>>
    >,
    render: (ctx) => {
      const inv = typeof ctx?.inv === 'string' ? ctx.inv : null;
      return createElement(LazyClaimsRowModule, { investigationId: inv });
    },
    backendSourceGroups: [],
  },
  placeholderEntry({
    id: 'echelon.paradox',
    category: 'echelon',
    subcategory: 'Paradox',
    label: 'Paradox Queue',
    description: 'Public-stated vs observed contradictions awaiting investigation.',
    defaultSize: { w: 6, h: 2 },
  }),
  {
    id: 'echelon.forge',
    category: 'echelon',
    subcategory: 'Forge',
    label: 'Forge Console',
    description: 'Pattern proposals from the Forge intelligence layer.',
    defaultSize: { w: 12, h: 2 },
    // Sprint-5a session-2 swap: placeholder → real ForgeLane.
    component: lazy(async () => {
      const mod = await import('../components/workspace/ForgeLane');
      return { default: mod.default };
    }),
    backendSourceGroups: [],
  },

  // ── Geopolitical ────────────────────────────────────────────────────
  {
    id: 'geo.conflicts',
    category: 'geopolitical',
    subcategory: 'Conflicts',
    label: 'Conflict Pulse',
    description: 'Active-conflict event density across regions, 72h rolling.',
    defaultSize: { w: 6, h: 2 },
    // Cycle 101.1 sprint-2 Task 2.4 — real ConflictsPulseModule.
    component: lazy(async () => {
      const mod = await import('../components/workspace/modules/ConflictsPulseModule');
      return { default: mod.default };
    }),
    backendSourceGroups: [
      'conflict_event',
      'conflict_alert',
      'conflict_intelligence',
      'ucdp_violence_event',
    ],
  },
  {
    id: 'geo.military',
    category: 'geopolitical',
    subcategory: 'Military',
    label: 'Military Movements',
    description: 'Observed fleet / air-wing / armor movements.',
    defaultSize: { w: 5, h: 2 },
    // Cycle 101.1 sprint-2 Task 2.5 — real MilitaryActivityModule.
    component: lazy(async () => {
      const mod = await import('../components/workspace/modules/MilitaryActivityModule');
      return { default: mod.default };
    }),
    backendSourceGroups: ['military_intelligence', 'military_posture'],
  },
  placeholderEntry({
    id: 'geo.unrest',
    category: 'geopolitical',
    subcategory: 'Unrest',
    label: 'Civil Unrest',
    description: 'Protest and unrest incidents, clustered by theatre.',
    defaultSize: { w: 4, h: 2 },
  }),
  {
    id: 'geo.intel',
    category: 'geopolitical',
    subcategory: 'Intelligence',
    label: 'Intel Brief Stream',
    description: 'Declassified / leaked intelligence bulletins.',
    defaultSize: { w: 6, h: 2 },
    // Cycle 101.1 sprint-2 Task 2.6 — real IntelligenceSignalsModule.
    component: lazy(async () => {
      const mod = await import('../components/workspace/modules/IntelligenceSignalsModule');
      return { default: mod.default };
    }),
    backendSourceGroups: [
      'news_intelligence',
      'osint_social',
      'cross_source_intelligence',
    ],
  },
  {
    id: 'geo.displacement',
    category: 'geopolitical',
    subcategory: 'Displacement',
    label: 'Displacement Flows',
    description: 'Refugee and IDP flow indicators.',
    defaultSize: { w: 6, h: 2 },
    // Cycle 101.1 sprint-2 Task 2.7 — real DisplacementFlowsModule.
    component: lazy(async () => {
      const mod = await import('../components/workspace/modules/DisplacementFlowsModule');
      return { default: mod.default };
    }),
    backendSourceGroups: [
      'displacement_data',
      'population_displacement',
      'humanitarian_crisis',
    ],
  },
  {
    id: 'geo.cyber',
    category: 'geopolitical',
    subcategory: 'Cyber',
    label: 'Cyber Posture',
    description: 'Observed state-linked cyber activity.',
    defaultSize: { w: 5, h: 2 },
    // Cycle 101.1 sprint-2 Task 2.10 — real CyberPostureModule.
    component: lazy(async () => {
      const mod = await import('../components/workspace/modules/CyberPostureModule');
      return { default: mod.default };
    }),
    backendSourceGroups: ['cyber_threat_intel', 'cyber_threats'],
  },

  // ── Natural events ──────────────────────────────────────────────────
  placeholderEntry({
    id: 'nat.disasters',
    category: 'natural',
    subcategory: 'Natural Disasters',
    label: 'Disaster Feed',
    description: 'Active disaster events with magnitude and impact.',
    defaultSize: { w: 5, h: 2 },
  }),
  {
    id: 'nat.seismic',
    category: 'natural',
    subcategory: 'Seismology',
    label: 'Seismic Swarm Monitor',
    description: 'Earthquake cluster detection with precursor analysis.',
    defaultSize: { w: 5, h: 2 },
    // Cycle 101.1 sprint-2 Task 2.8 — real SeismicSwarmModule.
    component: lazy(async () => {
      const mod = await import('../components/workspace/modules/SeismicSwarmModule');
      return { default: mod.default };
    }),
    backendSourceGroups: ['geophysical_hazard'],
  },
  {
    id: 'nat.climate',
    category: 'natural',
    subcategory: 'Climate',
    label: 'Climate Anomalies',
    description: 'Temperature and precipitation anomalies vs baseline.',
    defaultSize: { w: 5, h: 2 },
    // Cycle 101.1 sprint-2 Task 2.9 — real ClimateAnomaliesModule.
    component: lazy(async () => {
      const mod = await import('../components/workspace/modules/ClimateAnomaliesModule');
      return { default: mod.default };
    }),
    backendSourceGroups: [
      'climate_monitoring',
      'climate_events',
      'climate_news',
      'environmental_monitoring',
      'natural_events',
    ],
  },
  placeholderEntry({
    id: 'nat.wildfires',
    category: 'natural',
    subcategory: 'Wildfires',
    label: 'Wildfire Activity',
    description: 'FIRMS active-fire detections, hectare estimates.',
    defaultSize: { w: 5, h: 2 },
  }),

  // ── Economy & markets ───────────────────────────────────────────────
  {
    id: 'econ.economic',
    category: 'economy',
    subcategory: 'Economic',
    label: 'Macro Indicators',
    description: 'PMI, CPI, employment — leading indicators.',
    defaultSize: { w: 6, h: 2 },
    // Cycle 101.1 sprint-2 Task 2.3 — real MacroIndicatorsModule.
    component: lazy(async () => {
      const mod = await import('../components/workspace/modules/MacroIndicatorsModule');
      return { default: mod.default };
    }),
    backendSourceGroups: ['macro_economic'],
  },
  {
    id: 'econ.markets',
    category: 'economy',
    subcategory: 'Markets',
    label: 'Market Ticker',
    description: 'FX, equity, commodity ticker with σ-breaks flagged.',
    defaultSize: { w: 5, h: 2 },
    // Sprint-5b session-3 swap: placeholder → real MarketsModule.
    component: lazy(async () => {
      const mod = await import('../components/workspace/modules/MarketsModule');
      return { default: mod.default };
    }),
    backendSourceGroups: ['market_data'],
  },
  {
    id: 'econ.trade',
    category: 'economy',
    subcategory: 'Trade',
    label: 'Trade Flow',
    description: 'Bilateral trade volume shifts and tariff events.',
    defaultSize: { w: 7, h: 2 },
    // Cycle 101.1 sprint-2 Task 2.1 — real TradeFlowModule.
    component: lazy(async () => {
      const mod = await import('../components/workspace/modules/TradeFlowModule');
      return { default: mod.default };
    }),
    backendSourceGroups: ['trade_policy', 'commodity_futures'],
  },
  {
    id: 'econ.supply',
    category: 'economy',
    subcategory: 'Supply Chain',
    label: 'Supply Chain Stress',
    description: 'Inventory, freight, manufacturing-lead-time indicators.',
    defaultSize: { w: 6, h: 2 },
    // Cycle 101.1 sprint-2 Task 2.2 — real SupplyChainStressModule.
    component: lazy(async () => {
      const mod = await import('../components/workspace/modules/SupplyChainStressModule');
      return { default: mod.default };
    }),
    backendSourceGroups: ['supply_chain_intelligence'],
  },
  placeholderEntry({
    id: 'econ.predictions',
    category: 'economy',
    subcategory: 'Predictions',
    label: 'Prediction Markets',
    description: 'Polymarket, Kalshi, Metaculus priors on active theatres.',
    defaultSize: { w: 6, h: 2 },
  }),

  // ── Infrastructure & transport ──────────────────────────────────────
  {
    id: 'infra.aviation',
    category: 'infrastructure',
    subcategory: 'Aviation',
    label: 'Aviation Pulse',
    description: 'NOTAMs, FIR closures, unscheduled diversions.',
    defaultSize: { w: 6, h: 2 },
    // Cycle 101.1 sprint-2 Task 2.11 — real AviationPulseModule.
    component: lazy(async () => {
      const mod = await import('../components/workspace/modules/AviationPulseModule');
      return { default: mod.default };
    }),
    backendSourceGroups: ['aviation_intelligence', 'airport_delay'],
  },
  {
    id: 'infra.maritime',
    category: 'infrastructure',
    subcategory: 'Maritime',
    label: 'Maritime Traffic',
    description: 'AIS gaps, port events, choke-point density.',
    defaultSize: { w: 6, h: 2 },
    // Cycle-106 sprint-3 swap: placeholder → real MaritimeModule.
    // Visual + functional boundary distinct from infra.vessels (god's-eye
    // chokepoint map) — see SDD §1.4 Component C.
    component: lazy(async () => {
      const mod = await import('../components/workspace/modules/MaritimeModule');
      return { default: mod.default };
    }),
    backendSourceGroups: [],
  },
  {
    id: 'infra.vessels',
    category: 'infrastructure',
    subcategory: 'Vessel Tracker',
    label: 'Vessel Tracker',
    description:
      "God's-eye chokepoint view with live-simulated AIS vessels. Hormuz, Suez, Malacca, Bab-el-Mandeb, Bosphorus, Panama.",
    defaultSize: { w: 8, h: 3 },
    // Sprint-4 session-2 swap: placeholder → real VesselTracker. Shared by
    // FINANCE + MARITIME modes per SDD §5.2 shared-module invariant.
    component: lazy(async () => {
      const mod = await import('../components/workspace/modules/VesselTracker');
      return { default: mod.default };
    }),
    backendSourceGroups: [],
  },
  placeholderEntry({
    id: 'infra.infrastructure',
    category: 'infrastructure',
    subcategory: 'Infrastructure',
    label: 'Infrastructure Events',
    description: 'Grid, cable, pipeline, water incidents.',
    defaultSize: { w: 5, h: 2 },
  }),

  // ── Other ───────────────────────────────────────────────────────────
  placeholderEntry({
    id: 'other.news',
    category: 'other',
    subcategory: 'News',
    label: 'News Wire',
    description: 'Breaking news with source cross-validation.',
    defaultSize: { w: 5, h: 2 },
  }),
  placeholderEntry({
    id: 'other.research',
    category: 'other',
    subcategory: 'Research',
    label: 'Research Notes',
    description: 'Academic and institutional publications.',
    defaultSize: { w: 5, h: 2 },
  }),
  placeholderEntry({
    id: 'other.positive',
    category: 'other',
    subcategory: 'Positive Events',
    label: 'Positive Events',
    description: 'Countervailing positive developments.',
    defaultSize: { w: 4, h: 2 },
  }),
  placeholderEntry({
    id: 'other.giving',
    category: 'other',
    subcategory: 'Giving',
    label: 'Giving Tracker',
    description: 'Aid, humanitarian response, fundraising.',
    defaultSize: { w: 4, h: 2 },
  }),
];

// ── Derived indexes ─────────────────────────────────────────────────────

export const MODULE_IDS: readonly ModuleId[] = MODULE_REGISTRY.map((m) => m.id);

const REGISTRY_BY_ID = new Map<ModuleId, ModuleRegistryEntry>(
  MODULE_REGISTRY.map((entry) => [entry.id, entry] as const)
);

// ── Helpers ─────────────────────────────────────────────────────────────

/** Look up a registry entry by id. Returns `undefined` for unknown ids. */
export function getModuleEntry(id: ModuleId): ModuleRegistryEntry | undefined {
  return REGISTRY_BY_ID.get(id);
}

/** Type guard — narrows `unknown` to `ModuleId`. */
export function isModuleId(value: unknown): value is ModuleId {
  return typeof value === 'string' && REGISTRY_BY_ID.has(value as ModuleId);
}

/** Return all modules in a given category. Stable order (registry declaration order). */
export function getModulesByCategory(
  category: ModuleCategory
): readonly ModuleRegistryEntry[] {
  return MODULE_REGISTRY.filter((m) => m.category === category);
}
